import * as Ant from "antd";
import React from "react";
import styled from "styled-components";

import { Device } from "~/Device";
import { Application } from "~/Device/Application";
import { Platform } from "~/Device/Platform";
import * as Driver from "~/Driver";
import * as ErrorMessage from "~/ErrorMessage";
import { Order } from "~/Order";
import * as Tooltip from "~/Tooltip";

export function DeviceType({ device, ...driver }: Driver.Table.Datum) {
  return device ? (
    <DeviceType.Found device={device} driver={driver} />
  ) : (
    <DeviceType.Missing />
  );
}

export namespace DeviceType {
  export const column = (filter: Driver.Table.Filter) => ({
    ...Settings.basics(),
    ...Settings.filtering(filter),
    ...Settings.sorting(),
    ...Settings.rendering(),
  });

  namespace Settings {
    export const basics = () => ({ title: "Device Type", dataIndex: "device" });
    export const filtering = (filter: Driver.Table.Filter) => ({
      onFilter: (_: unknown, { device }: Driver.Driver) => {
        const { device: deviceFilters } = filter.filter;
        const { version, isOutdated } = Device.isOutdated({
          device: device ?? ({} as Partial<Application & Platform>),
        });
        // => true if version is missing + missing filter is enabled
        return (
          (deviceFilters?.isMissingApplication && !version) ||
          // or outdated + outdated filter is enabled
          (deviceFilters?.isOutdated && isOutdated)
        );
      },
      filteredValue: Filter.isActive(filter) ? ["filtered"] : [],
      filterDropdown: ({
        clearFilters: clear,
        confirm,
      }: {
        readonly confirm: () => void;
        readonly clearFilters?: () => void;
      }) => <Filter {...filter} clear={clear} confirm={confirm} />,
    });

    export const sorting = () => ({
      sorter: (a: Driver.Table.Datum, b: Driver.Table.Datum) =>
        Order.optionally(Device.compare)(
          a.device && { device: a.device },
          b.device && { device: b.device }
        ),
    });

    export const rendering = () => ({
      render: (_: unknown, datum: Driver.Table.Datum) => (
        <DeviceType {...datum} />
      ),
    });
  }

  export function Found(props: Found.Props) {
    return (
      <>
        <Application {...props} />
        <Platform {...props} />
      </>
    );
  }

  namespace Found {
    export type Props = Device & { readonly driver: Driver.Driver };
  }

  function Application({ device, driver }: Found.Props) {
    const { version, latest, isOutdated } = Device.isOutdated({ device });
    if (!isOutdated) return <Device.Application application={{ version }} />;
    if (!version) return <Application.Missing />;
    return (
      <Application.Outdated latest={latest} version={version} driver={driver} />
    );
  }

  namespace Application {
    export const Outdated = ({
      version,
      latest,
      driver,
    }: Omit<ReturnType<typeof Device.isOutdated>, "isOutdated"> & {
      readonly driver: Driver.Driver;
    }) => {
      const message = latest ? (
        <>
          <Driver.Name.Component {...driver} noLink /> isn't on the latest
          version of the app,&nbsp;
          <Device.Application application={{ ...latest }} />
        </>
      ) : (
        <>
          <Driver.Name.Component {...driver} noLink />
          &nbsp;has an unknown device type, which means&nbsp;
          <Device.Application application={{ version }} /> might be out of date
        </>
      );

      return (
        <Tooltip.Component Content={() => message}>
          <ErrorMessage.Component icon danger>
            <Device.Application application={{ version }} />
          </ErrorMessage.Component>
        </Tooltip.Component>
      );
    };

    export const Missing = () => (
      <ErrorMessage.Component icon danger>
        Missing app
      </ErrorMessage.Component>
    );
  }

  function Platform({ device }: Device) {
    return (
      <Driver.Table.Detail>
        {device.platform ? (
          <Device.Platform platform={device.platform} />
        ) : (
          <Platform.Unknown />
        )}
      </Driver.Table.Detail>
    );
  }

  namespace Platform {
    export const Unknown = () => (
      <ErrorMessage.Component icon danger>
        Unknown platform
      </ErrorMessage.Component>
    );
  }

  export const Missing = () => (
    <ErrorMessage.Component icon danger>
      No device
    </ErrorMessage.Component>
  );

  export type Filter = {
    readonly device: {
      readonly isMissingApplication?: boolean;
      readonly isOutdated?: boolean;
    };
  };

  export function Filter({
    filter,
    clear,
    confirm,
  }: Driver.Table.Filter.Props) {
    const controls = {
      confirm,
      ...(Filter.isActive({ filter }) && {
        clear: () => {
          clear?.();
          pipe(
            { filter },
            Driver.Table.Filter.Update.DeviceType.isOutdated(),
            Driver.Table.Filter.Update.DeviceType.isMissingApplication(),
            filter.update
          );
        },
      }),
    };

    const isOutdated = (
      <Ant.Checkbox
        onChange={({ target: { checked } }) =>
          pipe(
            { filter },
            Driver.Table.Filter.Update.DeviceType.isOutdated(checked),
            filter.update
          )
        }
      >
        Outdated app
      </Ant.Checkbox>
    );

    const isMissingApplication = (
      <Ant.Checkbox
        onChange={({ target: { checked } }) =>
          pipe(
            { filter },
            Driver.Table.Filter.Update.DeviceType.isMissingApplication(checked),
            filter.update
          )
        }
      >
        Missing app
      </Ant.Checkbox>
    );

    return (
      <Driver.Table.Filter filter={filter} {...controls}>
        <Driver.Table.Filter.Section>
          <Filter.Checkboxes>
            {isOutdated}
            <br />
            {isMissingApplication}
          </Filter.Checkboxes>
        </Driver.Table.Filter.Section>
      </Driver.Table.Filter>
    );
  }

  export namespace Filter {
    export const isActive = ({ filter }: Driver.Table.Filter): boolean =>
      !!filter.device?.isMissingApplication || !!filter.device?.isOutdated;

    export const applies = ({ filter }: Driver.Table.Filter) => (
      datum: Driver.Table.Datum
    ): boolean =>
      !isActive({ filter }) ||
      (filter.device?.isMissingApplication &&
        !datum.device?.application?.version) ||
      !!(
        filter.device?.isOutdated &&
        datum.device &&
        Device.isOutdated({ device: datum.device }).isOutdated
      );

    export const Checkboxes = styled.div`
      padding: 8px;
    `;
  }
}
